import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, X, ChevronDown, SkipForward, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWorkflow } from "../../../context/WorkflowContext";
import { useWorkflowExecution } from "../../../context/WorkflowExecutionContext";
import ExecutionDetailPane from "./ExecutionDetailPane";

const ICONS = {
    success: <CheckCircle size={13} className="text-green-500" />,
    error: <XCircle size={13} className="text-red-500" />,
    running: <Loader2 size={13} className="text-blue-500 animate-spin" />,
    skipped: <SkipForward size={13} className="text-gray-400" />,
};
const PENDING_DOT = <div className="w-[13px] h-[13px] rounded-full border border-gray-300" />;

const getItemCount = (r) => { const a = r?.output || r?.result; return Array.isArray(a) ? a.length : r ? 1 : null; };

export default function ExecutionPanel({ onClose }) {
    const { steps } = useWorkflow();
    const { stepExecutionResult, stepExecutionLogs, stepIdRunning, failedStepId, failedStepError, isExecutingTask, cancelExecution } = useWorkflowExecution();
    const { t } = useTranslation();
    const [selectedIdx, setSelectedIdx] = useState(null);
    const prevRunningRef = React.useRef(null);

    const stepResults = useMemo(() => steps.map((step, idx) => {
        const result = stepExecutionResult?.[idx] ?? null;
        let status = "pending";
        if (step.id === stepIdRunning) status = "running";
        else if (step.id === failedStepId) status = "error";
        else if (result?.status === "error") status = "error";
        else if (result?.status === "skipped") status = "skipped";
        else if (result != null) status = "success";
        return { step, result, status, items: getItemCount(result) };
    }), [steps, stepExecutionResult, stepIdRunning, failedStepId]);

    useEffect(() => {
        const wasRunning = prevRunningRef.current;
        prevRunningRef.current = !!stepIdRunning || isExecutingTask;
        if (wasRunning && !prevRunningRef.current && stepExecutionResult) {
            const idx = stepResults.findIndex((r) => r.status === "success" || r.status === "error");
            if (idx >= 0) setSelectedIdx(idx);
        }
    }, [stepIdRunning, isExecutingTask, stepExecutionResult, stepResults]);

    useEffect(() => {
        if (selectedIdx === null && stepResults.length === 1 && stepResults[0].result != null) setSelectedIdx(0);
    }, [selectedIdx, stepResults]);

    const getLogs = useCallback((sel) => {
        if (!sel || !stepExecutionLogs) return null;
        // New format: logs keyed by stepId
        if (stepExecutionLogs[sel.step.id]) return stepExecutionLogs[sel.step.id];
        // Legacy format: logs keyed by index position
        const idx = steps.findIndex((s) => s.id === sel.step.id);
        if (idx >= 0 && stepExecutionLogs[idx]) return stepExecutionLogs[idx];
        return null;
    }, [stepExecutionLogs, steps]);

    if (!stepExecutionResult && !stepIdRunning && !isExecutingTask) return null;

    const isRunning = !!stepIdRunning || isExecutingTask;
    const hasError = !!failedStepId;
    const totalMs = stepResults.reduce((sum, r) => sum + (r.result?.duration || 0), 0);
    const headerStatus = isRunning ? "Running..." : hasError ? "Failed" : "Done";
    const selected = selectedIdx != null ? stepResults[selectedIdx] : null;

    return (
        <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 shadow-lg flex flex-col h-[60%] md:h-[40%] max-h-[70%] md:max-h-[50%]">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-gray-700 truncate">
                        {headerStatus}{!isRunning && totalMs > 0 ? ` — ${(totalMs / 1000).toFixed(1)}s` : ""}
                        {hasError && !isRunning ? ` — ${failedStepError || "Error"}` : ""}
                    </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {isRunning && <button onClick={cancelExecution} title={t("executionPanel.cancelExecution")}
                        className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-red-600 hover:bg-red-50 rounded border border-red-200">
                        <Square size={10} className="fill-current" /> {t("common.cancel")}
                    </button>}
                    <button onClick={onClose} className="p-0.5 hover:bg-gray-100 rounded"><X size={14} className="text-gray-400" /></button>
                </div>
            </div>
            <div className="md:hidden flex-shrink-0 border-b border-gray-200 overflow-x-auto">
                <div className="flex gap-1 p-1.5">
                    {stepResults.map(({ step, status }, idx) => (
                        <button key={step.id} onClick={() => setSelectedIdx(idx)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] whitespace-nowrap flex-shrink-0 border ${selectedIdx === idx ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
                            {ICONS[status] || PENDING_DOT} {step.name || step.type}
                        </button>))}
                </div>
            </div>
            <div className="flex-1 flex min-h-0">
                <div className="hidden md:block w-[280px] border-r border-gray-200 overflow-y-auto flex-shrink-0">
                    {stepResults.map(({ step, result, status, items }, idx) => (
                        <button key={step.id} onClick={() => setSelectedIdx(idx)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 border-b border-gray-100 ${selectedIdx === idx ? "bg-blue-50" : ""}`}>
                            {ICONS[status] || PENDING_DOT}
                            <span className="text-xs text-gray-800 truncate flex-1">{step.name || step.type}</span>
                            {result?.duration && <span className="text-[10px] text-gray-400">{result.duration}ms</span>}
                            {items != null && <span className="text-[10px] text-gray-400">{items}</span>}
                        </button>))}
                </div>
                <ExecutionDetailPane selected={selected} logs={getLogs(selected)} />
            </div>
        </motion.div>
    );
}
